import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authAPI } from '../services/api';
import { useAuthStore } from '../store/authStore';

export default function OAuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setAuth } = useAuthStore();
  const [error, setError] = useState<string | null>(null);
  // Prevent duplicate auth attempts due to effect re-runs
  const hasAttempted = useRef(false);

  useEffect(() => {
    const handleCallback = async () => {
      // Guard against duplicate API calls from effect re-runs
      if (hasAttempted.current) return;
      hasAttempted.current = true;
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const errorParam = searchParams.get('error');
      const isMock = searchParams.get('mock') === 'true';

      // Check for OAuth errors
      if (errorParam) {
        setError(`Authentication failed: ${errorParam}`);
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      // Handle mock mode
      if (isMock) {
        try {
          const response = await authAPI.handleCallback('mock_code', 'mock_state');
          if (response.success && response.data) {
            setAuth(response.data.token, response.data.user);
            navigate('/dashboard');
          }
        } catch (err) {
          console.error('Mock callback error:', err);
          setError('Mock authentication failed');
        }
        return;
      }

      if (!code || !state) {
        setError('Invalid callback parameters');
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      try {
        const response = await authAPI.handleCallback(code, state);

        if (response.success && response.data) {
          // Store auth data
          setAuth(response.data.token, response.data.user);

          // Redirect based on new user status
          if (response.data.is_new_user) {
            navigate('/dashboard?welcome=true');
          } else {
            navigate('/dashboard');
          }
        } else {
          setError(response.error?.message || 'Authentication failed');
          setTimeout(() => navigate('/login'), 3000);
        }
      } catch (err) {
        console.error('Callback error:', err);
        setError('An error occurred during authentication');
        setTimeout(() => navigate('/login'), 3000);
      }
    };

    handleCallback();
  }, [searchParams, navigate, setAuth]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">⚠️</div>
          <p className="text-gray-700">{error}</p>
          <p className="text-sm text-gray-500 mt-2">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ondeck-600 mx-auto mb-4"></div>
        <p className="text-gray-700">Completing sign in...</p>
      </div>
    </div>
  );
}
